import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Navigate, useOutletContext } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { AiVisBrandsIcon } from './AiVisNavIcons';
import { canAccessBrands, canDeleteCampaigns } from './aivisRoles';
import './ai-vis.css';

function displayHostname(url) {
  if (!url) return '';
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`);
    return u.hostname.replace(/^www\./i, '') || url;
  } catch {
    return url;
  }
}

/** Convert a #rrggbb hex string to [hue, saturation] in HSL (0-360, 0-100). */
function _hexToHueSat(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let hue = 0;
  if (d > 0) {
    if (max === r) hue = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) hue = ((b - r) / d + 2) / 6;
    else hue = ((r - g) / d + 4) / 6;
  }
  const l = (max + min) / 2;
  const sat = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  return [Math.round(hue * 360), Math.round(sat * 100)];
}

/**
 * Return a pastel banner background for a campaign tile.
 * When brand_color_hex is present the hue is taken from the actual logo color;
 * otherwise a deterministic hash of the campaign ID/name is used as a fallback.
 */
function brandCoverTint(campaign) {
  const hex = campaign.brand_color_hex;
  if (hex && /^#[0-9a-fA-F]{6}$/.test(hex)) {
    const [hue, rawSat] = _hexToHueSat(hex);
    const sat = Math.min(55, Math.max(30, rawSat));
    return `hsl(${hue} ${sat}% 90%)`;
  }
  // Fallback: stable pseudo-random pastel derived from campaign identity.
  const key = `${campaign.id}|${campaign.client_name || ''}|${campaign.client_website || ''}`;
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const hue = Math.abs(h) % 360;
  const sat = 38 + (Math.abs(h >> 8) % 28);
  const light = 88 + (Math.abs(h >> 16) % 7);
  return `hsl(${hue} ${sat}% ${light}%)`;
}

function linesToDomainList(text) {
  if (!text || !String(text).trim()) return [];
  return String(text)
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

const SCHED_FREQ_LABELS = {
  quarter_yearly: 'Quarterly',
  monthly: 'Monthly',
  bi_weekly: 'Bi-weekly',
  weekly: 'Weekly',
  three_days: 'Every 3 days',
  daily: 'Daily',
};

const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/** Human-readable schedule line for success toasts (matches API campaign detail). */
function formatCampaignSchedule(c) {
  if (!c) return 'your configured schedule';
  const parts = [];
  parts.push(SCHED_FREQ_LABELS[c.frequency] || c.frequency || 'scheduled');
  if (
    (c.frequency === 'weekly' || c.frequency === 'bi_weekly') &&
    c.preferred_weekday != null &&
    c.preferred_weekday >= 0 &&
    c.preferred_weekday <= 6
  ) {
    parts.push(`on ${WEEKDAY_NAMES[c.preferred_weekday]}`);
  }
  if (c.next_run_at) {
    try {
      const d = new Date(c.next_run_at);
      parts.push(`next run ${d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}`);
    } catch {
      /* ignore */
    }
  }
  return parts.join(', ');
}

/** Narrow no-break before final period so it stays with the last word (avoid orphan “.”). */
function tieClosingPeriod(text) {
  const s = (text || '').trim();
  if (!s.endsWith('.')) return s;
  return `${s.slice(0, -1).trimEnd()}\u202F.`;
}

function DisplayNameInfoButton() {
  const btnRef = useRef(null);
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const updatePos = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({
      top: r.top - 10,
      left: r.left + r.width / 2,
    });
  }, []);

  useEffect(() => {
    if (!show) return undefined;
    function onScrollOrResize() {
      updatePos();
    }
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [show, updatePos]);

  const tip = show
    ? createPortal(
        <div
          className="aivis-brand-tip-capsule"
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            transform: 'translate(-50%, -100%)',
            zIndex: 10050,
          }}
          role="tooltip"
        >
          The display name is not used for matching.
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="aivis-brand-panel-hint-icon"
        aria-label="About display name"
        onMouseEnter={() => {
          updatePos();
          setShow(true);
        }}
        onMouseLeave={() => setShow(false)}
        onFocus={() => {
          updatePos();
          setShow(true);
        }}
        onBlur={() => setShow(false)}
      >
        i
      </button>
      {tip}
    </>
  );
}

const BRAND_PANEL_TRANSITION_MS = 320;

function BrandTile({
  campaign,
  isSelected,
  onEdit,
  onRemove,
  onUnarchive,
  onHardDelete,
  showDelete,
  showDeleteLocked,
  menuOpen,
  onMenuToggle,
  menuRef,
}) {
  const cover = brandCoverTint(campaign);
  const domain = displayHostname(campaign.client_website);
  const mentions = typeof campaign.mention_count === 'number' ? campaign.mention_count : 0;
  const archived = !!campaign.is_archived;

  // The tile card clips its own contents (rounded corners), so the "..." menu
  // is positioned via the viewport (position: fixed) rather than relative to
  // the card — otherwise it gets cropped by the card's own border/overflow
  // instead of floating above everything on the page.
  const triggerRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);

  useLayoutEffect(() => {
    if (!menuOpen) {
      setMenuPos(null);
      return undefined;
    }
    function updatePos() {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setMenuPos({
        bottom: window.innerHeight - rect.top + 6,
        right: window.innerWidth - rect.right,
      });
    }
    updatePos();
    window.addEventListener('resize', updatePos);
    // Close (rather than reposition) on scroll — the menu is short-lived, so
    // simply dismissing it avoids stale positioning while the page moves.
    window.addEventListener('scroll', onMenuToggle, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', onMenuToggle, true);
    };
  }, [menuOpen, onMenuToggle]);

  let statusLabel = 'Active';
  let statusClass = 'aivis-status-capsule--active';
  if (archived) {
    statusLabel = 'Archived';
    statusClass = 'aivis-status-capsule--inactive';
  } else if (campaign.enabled === false) {
    statusLabel = 'Paused';
    statusClass = 'aivis-status-capsule--paused';
  }

  return (
    <article
      className={`aivis-brand-tile${archived ? ' aivis-brand-tile--archived' : ''}`}
      role="button"
      tabIndex={0}
      aria-label={`Edit brand ${campaign.client_name || campaign.name || ''}`}
      onClick={() => onEdit()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onEdit();
        }
      }}
    >
      <div className="aivis-brand-tile-cover" style={{ background: cover }}>
        {isSelected ? (
          <span className="aivis-brand-tile-badge">Your brand</span>
        ) : null}
      </div>
      <div className="aivis-brand-tile-body">
        <div className="aivis-brand-tile-logo-row">
          {campaign.client_logo_url ? (
            <img
              src={campaign.client_logo_url}
              alt=""
              className="aivis-brand-tile-logo"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <span className="aivis-brand-tile-logo-fallback" aria-hidden>
              {(campaign.client_name || '?').slice(0, 1).toUpperCase()}
            </span>
          )}
        </div>
        <div className="aivis-brand-tile-text">
          <div className="aivis-brand-tile-name-row">
            <h3 className="aivis-brand-tile-name">{campaign.client_name || campaign.name}</h3>
            <span className={`aivis-status-capsule ${statusClass}`}>
              {statusLabel}
            </span>
          </div>
          <p className="aivis-brand-tile-mentions">
            {mentions} Mention{mentions === 1 ? '' : 's'}
          </p>
          {domain ? <span className="aivis-brand-tile-domain-pill">{domain}</span> : null}
        </div>
        <div
          className="aivis-brand-tile-actions"
          ref={menuRef}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            ref={triggerRef}
            className="aivis-brand-tile-more"
            aria-expanded={menuOpen}
            aria-haspopup="true"
            aria-label="Brand actions"
            onClick={(e) => {
              e.stopPropagation();
              onMenuToggle();
            }}
          >
            <span className="aivis-brand-tile-more-dots" aria-hidden>
              ···
            </span>
          </button>
          {menuOpen && menuPos ? (
            <div
              className="aivis-brand-tile-menu"
              role="menu"
              style={{ bottom: menuPos.bottom, right: menuPos.right }}
            >
              <button
                type="button"
                className="aivis-brand-tile-menu-item"
                role="menuitem"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5Z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Edit
              </button>
              {archived ? (
                showDelete ? (
                  <>
                    <button
                      type="button"
                      className="aivis-brand-tile-menu-item"
                      role="menuitem"
                      onClick={(e) => {
                        e.stopPropagation();
                        onUnarchive();
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                        <path d="M3 7l9-4 9 4-9 4-9-4Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M3 7v10l9 4 9-4V7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Unarchive
                    </button>
                    <button
                      type="button"
                      className="aivis-brand-tile-menu-item aivis-brand-tile-menu-item-danger"
                      role="menuitem"
                      onClick={(e) => {
                        e.stopPropagation();
                        onHardDelete();
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                        <path
                          d="M3 6h18M8 6V4h8v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6"
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                        />
                      </svg>
                      Delete permanently
                    </button>
                  </>
                ) : null
              ) : showDelete ? (
                <button
                  type="button"
                  className="aivis-brand-tile-menu-item aivis-brand-tile-menu-item-danger"
                  role="menuitem"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove();
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      d="M3 6h18M8 6V4h8v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                    />
                  </svg>
                  Remove
                </button>
              ) : null}
              {showDeleteLocked ? (
                <button
                  type="button"
                  className="aivis-brand-tile-menu-item aivis-brand-tile-menu-item-disabled"
                  role="menuitem"
                  disabled
                  title="Only company or system administrators can archive or delete clients."
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      d="M3 6h18M8 6V4h8v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                    />
                  </svg>
                  Remove (Admins only)
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
}

const FREQ_OPTIONS = [
  { value: 'daily',          label: 'Daily' },
  { value: 'three_days',     label: 'Every 3 days' },
  { value: 'weekly',         label: 'Weekly' },
  { value: 'bi_weekly',      label: 'Bi-weekly' },
  { value: 'monthly',        label: 'Monthly' },
  { value: 'quarter_yearly', label: 'Quarterly' },
];

function formatNextRun(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return iso;
  }
}

function BrandEditPanel({
  open,
  onClose,
  campaignId,
  coverTint,
  initialDetail,
  form,
  setForm,
  saving,
  loading,
  onSave,
  formError,
}) {
  const panelRef = useRef(null);
  const lastCampaignIdRef = useRef(null);
  if (campaignId) lastCampaignIdRef.current = campaignId;
  const effectiveCampaignId = campaignId || lastCampaignIdRef.current;

  const [rendered, setRendered] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (open && effectiveCampaignId) {
      setRendered(true);
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setEntered(true));
      });
      return () => cancelAnimationFrame(id);
    }
    setEntered(false);
    const t = window.setTimeout(() => {
      setRendered(false);
      lastCampaignIdRef.current = null;
    }, BRAND_PANEL_TRANSITION_MS);
    return () => window.clearTimeout(t);
  }, [open, effectiveCampaignId]);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!rendered || !effectiveCampaignId) return null;

  return (
    <div
      className={`aivis-brand-panel-root ${entered ? 'aivis-brand-panel-root--open' : ''}`}
      aria-hidden={!entered}
    >
      <button type="button" className="aivis-brand-panel-backdrop" aria-label="Close panel" onClick={onClose} />
      <div className="aivis-brand-panel" ref={panelRef} role="dialog" aria-modal="true" aria-labelledby="aivis-brand-panel-title">
        <div className="aivis-brand-panel-cover" style={{ background: coverTint }}>
          {initialDetail?.client_logo_url ? (
            <img
              src={initialDetail.client_logo_url}
              alt=""
              className="aivis-brand-panel-logo"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <span className="aivis-brand-panel-logo-fallback" aria-hidden>
              {(form.client_name || '?').slice(0, 1).toUpperCase()}
            </span>
          )}
        </div>

        <div className="aivis-brand-panel-scroll">
          {loading ? <p className="aivis-brand-panel-loading">Loading…</p> : null}
          <div className="aivis-brand-panel-field">
            <div className="aivis-brand-panel-label-row">
              <label id="aivis-brand-panel-title" htmlFor="aivis-brand-display-name">
                Display name
              </label>
              <DisplayNameInfoButton />
            </div>
            <input
              id="aivis-brand-display-name"
              className="aivis-brand-panel-input aivis-brand-panel-input-display"
              value={form.client_name}
              onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))}
              autoComplete="organization"
            />
          </div>

          <div className="aivis-brand-panel-field">
            <div className="aivis-brand-toggle-row">
              <span className="aivis-brand-toggle-label" id="aivis-brand-scheduled-runs-label">
                Run on schedule
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={form.enabled}
                aria-labelledby="aivis-brand-scheduled-runs-label"
                id="aivis-brand-scheduled-runs"
                className={`aivis-brand-toggle ${form.enabled ? 'aivis-brand-toggle--on' : ''}`}
                onClick={() => setForm((f) => ({ ...f, enabled: !f.enabled }))}
              >
                <span className="aivis-brand-toggle-thumb" aria-hidden />
              </button>
            </div>
            <p className="aivis-brand-panel-help">
              When off, scheduled runs are skipped and the client is not deleted. You can still use Run now on the
              Overview page.
            </p>
          </div>

          <div className="aivis-brand-panel-field">
            <label htmlFor="aivis-brand-tracked">Tracked names</label>
            <p className="aivis-brand-panel-help">
              Only tracked names and aliases are matched in AI answers to identify the brand. The display name is not
              used for tracking.
            </p>
            <textarea
              id="aivis-brand-tracked"
              className="aivis-brand-panel-textarea"
              rows={4}
              value={form.alternative_names}
              onChange={(e) => setForm((f) => ({ ...f, alternative_names: e.target.value }))}
              placeholder="One per line or comma-separated"
            />
          </div>

          <div className="aivis-brand-panel-field">
            <label htmlFor="aivis-brand-overview">Client overview (required)</label>
            <p className="aivis-brand-panel-help">
              Background for PR scoring: who the client is, market, positioning. Sent to the Gemini prompt with each
              source URL.
            </p>
            <textarea
              id="aivis-brand-overview"
              className="aivis-brand-panel-textarea"
              rows={5}
              value={form.client_overview}
              onChange={(e) => setForm((f) => ({ ...f, client_overview: e.target.value }))}
              placeholder="e.g. B2B SaaS for field service teams…"
              required
            />
          </div>

          <div className="aivis-brand-panel-field">
            <label htmlFor="aivis-brand-core-offerings">Core offering(s) (required)</label>
            <p className="aivis-brand-panel-help">Products or services used to judge whether a roundup is a good pitch fit.</p>
            <textarea
              id="aivis-brand-core-offerings"
              className="aivis-brand-panel-textarea"
              rows={4}
              value={form.core_offerings}
              onChange={(e) => setForm((f) => ({ ...f, core_offerings: e.target.value }))}
              placeholder="e.g. Scheduling software, mobile apps…"
              required
            />
          </div>

          <div className="aivis-brand-panel-field">
            <label htmlFor="aivis-brand-website">Client website</label>
            <input
              id="aivis-brand-website"
              className="aivis-brand-panel-input"
              type="url"
              value={form.client_website}
              onChange={(e) => setForm((f) => ({ ...f, client_website: e.target.value }))}
            />
          </div>

          <div className="aivis-brand-panel-field">
            <label htmlFor="aivis-brand-dnc">Do not contact domains</label>
            <p className="aivis-brand-panel-help">
              These domains still appear in Cited Domains and Cited URLs. We will not send them to prospecting outreach. Enter one domain per line.
            </p>
            <textarea
              id="aivis-brand-dnc"
              className="aivis-brand-panel-textarea"
              rows={3}
              value={form.do_not_contact_domains}
              onChange={(e) => setForm((f) => ({ ...f, do_not_contact_domains: e.target.value }))}
              placeholder={'competitor.com\nother.com'}
            />
          </div>

          {/* ── Schedule section ── */}
          <div className="aivis-brand-panel-section-divider">
            <span className="aivis-brand-panel-section-label">Schedule</span>
          </div>

          <div className="aivis-brand-panel-field">
            <label htmlFor="aivis-brand-frequency">Frequency</label>
            <select
              id="aivis-brand-frequency"
              className="aivis-brand-panel-input"
              value={form.frequency}
              onChange={(e) => setForm((f) => ({ ...f, frequency: e.target.value }))}
            >
              {FREQ_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {(form.frequency === 'weekly' || form.frequency === 'bi_weekly') && (
            <div className="aivis-brand-panel-field">
              <label htmlFor="aivis-brand-weekday">Preferred day</label>
              <select
                id="aivis-brand-weekday"
                className="aivis-brand-panel-input"
                value={form.preferred_weekday ?? 5}
                onChange={(e) => setForm((f) => ({ ...f, preferred_weekday: Number(e.target.value) }))}
              >
                {WEEKDAY_NAMES.map((d, i) => (
                  <option key={i} value={i}>{d}</option>
                ))}
              </select>
            </div>
          )}

          <div className="aivis-brand-panel-field">
            <label htmlFor="aivis-brand-runs">
              {(form.frequency === 'weekly' || form.frequency === 'bi_weekly') ? 'Runs on preferred day' : 'Runs per period'}
            </label>
            <p className="aivis-brand-panel-help">
              How many times to run within each {(form.frequency === 'weekly' || form.frequency === 'bi_weekly') ? 'week' : 'period'}. Each run processes all prompts.
            </p>
            <input
              id="aivis-brand-runs"
              className="aivis-brand-panel-input"
              type="number"
              min={1}
              max={10}
              value={(form.frequency === 'weekly' || form.frequency === 'bi_weekly') ? form.runs_on_preferred_day : form.runs_per_period}
              onChange={(e) => {
                const v = Math.max(1, Number(e.target.value) || 1);
                if (form.frequency === 'weekly' || form.frequency === 'bi_weekly') {
                  setForm((f) => ({ ...f, runs_on_preferred_day: v }));
                } else {
                  setForm((f) => ({ ...f, runs_per_period: v }));
                }
              }}
            />
          </div>

          {((form.frequency === 'weekly' || form.frequency === 'bi_weekly') ? form.runs_on_preferred_day : form.runs_per_period) > 1 && (
            <div className="aivis-brand-panel-field">
              <label htmlFor="aivis-brand-gap">Gap between runs</label>
              <p className="aivis-brand-panel-help">Hours between each run within the same period.</p>
              <div className="aivis-brand-panel-inline-input">
                <input
                  id="aivis-brand-gap"
                  className="aivis-brand-panel-input aivis-brand-panel-input-short"
                  type="number"
                  min={1}
                  max={24}
                  value={form.run_gap_hours}
                  onChange={(e) => setForm((f) => ({ ...f, run_gap_hours: Math.max(1, Number(e.target.value) || 1) }))}
                />
                <span className="aivis-brand-panel-input-unit">hour{form.run_gap_hours === 1 ? '' : 's'}</span>
              </div>
            </div>
          )}

          <div className="aivis-brand-panel-schedule-info">
            {initialDetail?.client_timezone && (
              <div className="aivis-brand-panel-info-row">
                <span className="aivis-brand-panel-info-label">Timezone</span>
                <span className="aivis-brand-panel-info-value">{initialDetail.client_timezone}</span>
              </div>
            )}
            {initialDetail?.next_run_at && (
              <div className="aivis-brand-panel-info-row">
                <span className="aivis-brand-panel-info-label">Next run</span>
                <span className="aivis-brand-panel-info-value">{formatNextRun(initialDetail.next_run_at)}</span>
              </div>
            )}
            {initialDetail?.last_run_completed_at && (
              <div className="aivis-brand-panel-info-row">
                <span className="aivis-brand-panel-info-label">Last run</span>
                <span className="aivis-brand-panel-info-value">{formatNextRun(initialDetail.last_run_completed_at)}</span>
              </div>
            )}
            {initialDetail?.created_at && (
              <div className="aivis-brand-panel-info-row">
                <span className="aivis-brand-panel-info-label">Client since</span>
                <span className="aivis-brand-panel-info-value">
                  {new Date(initialDetail.created_at).toLocaleDateString(undefined, {
                    year: 'numeric', month: 'short', day: 'numeric',
                  })}
                </span>
              </div>
            )}
          </div>

          {formError ? <div className="aivis-auth-error aivis-brand-panel-error">{formError}</div> : null}
        </div>

        <div className="aivis-brand-panel-footer">
          <button type="button" className="aivis-btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="aivis-btn-primary" onClick={onSave} disabled={saving || loading}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BrandsPage() {
  const { user } = useAuth();
  const { campaigns, refreshCampaigns, selectedCampaignId, selectedOrgId } = useOutletContext();
  const [menuOpenId, setMenuOpenId] = useState(null);
  const menuRefs = useRef({});

  const [showArchived, setShowArchived] = useState(false);
  const [archivedCampaigns, setArchivedCampaigns] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);

  const [panelOpen, setPanelOpen] = useState(false);
  const [panelCampaignId, setPanelCampaignId] = useState(null);
  const [panelDetail, setPanelDetail] = useState(null);
  const [form, setForm] = useState({
    client_name: '',
    client_website: '',
    alternative_names: '',
    client_overview: '',
    core_offerings: '',
    additional_domains: '',
    do_not_contact_domains: '',
    enabled: true,
    frequency: 'weekly',
    preferred_weekday: 5,
    runs_per_period: 1,
    runs_on_preferred_day: 1,
    run_gap_hours: 1,
  });
  const [panelLoading, setPanelLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  // removeTarget: { campaign, mode } — mode 'choice' offers Archive vs Delete
  // (active clients); mode 'confirm-delete' is a plain hard-delete confirm
  // (already-archived clients, reached via the tile menu's "Delete permanently").
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removeChoice, setRemoveChoice] = useState('archive');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [toast, setToast] = useState(null);
  const [toastVisible, setToastVisible] = useState(false);
  const toastVisibleRef = useRef(false);

  const canView = canAccessBrands(user);
  const showDelete = canDeleteCampaigns(user);

  const pushToast = useCallback((message) => {
    toastVisibleRef.current = false;
    setToastVisible(false);
    setToast({ id: Date.now(), message });
  }, []);

  const sorted = useMemo(
    () => [...(campaigns || [])].sort((a, b) => (a.client_name || a.name || '').localeCompare(b.client_name || b.name || '')),
    [campaigns]
  );

  const loadArchivedCampaigns = useCallback(async () => {
    setArchivedLoading(true);
    try {
      const params = new URLSearchParams({ include_archived: 'true', page_size: '500' });
      if (selectedOrgId) params.set('organization', selectedOrgId);
      const { data } = await api.get(`/aivis/campaigns/?${params.toString()}`);
      const list = data.results ?? (Array.isArray(data) ? data : []);
      setArchivedCampaigns(list.filter((c) => c.is_archived));
    } catch {
      setArchivedCampaigns([]);
    } finally {
      setArchivedLoading(false);
    }
  }, [selectedOrgId]);

  useEffect(() => {
    if (!showArchived) return;
    loadArchivedCampaigns();
  }, [showArchived, loadArchivedCampaigns]);

  const sortedArchived = useMemo(
    () => [...archivedCampaigns].sort((a, b) => (a.client_name || a.name || '').localeCompare(b.client_name || b.name || '')),
    [archivedCampaigns]
  );

  const closeMenu = useCallback(() => setMenuOpenId(null), []);

  useEffect(() => {
    function onDocClick(e) {
      if (!menuOpenId) return;
      const ref = menuRefs.current[menuOpenId];
      if (ref && !ref.contains(e.target)) setMenuOpenId(null);
    }
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [menuOpenId]);

  useEffect(() => {
    toastVisibleRef.current = toastVisible;
  }, [toastVisible]);

  useEffect(() => {
    if (!toast) {
      toastVisibleRef.current = false;
      setToastVisible(false);
      return undefined;
    }
    toastVisibleRef.current = false;
    setToastVisible(false);
    let cancelled = false;
    const enter = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (cancelled) return;
        toastVisibleRef.current = true;
        setToastVisible(true);
      });
    });
    const hideTimer = window.setTimeout(() => {
      if (cancelled) return;
      toastVisibleRef.current = false;
      setToastVisible(false);
    }, 7200);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(enter);
      window.clearTimeout(hideTimer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast?.id]);

  const onToastTransitionEnd = useCallback((e) => {
    if (e.target !== e.currentTarget) return;
    if (e.propertyName !== 'transform' && e.propertyName !== 'opacity') return;
    if (!toastVisibleRef.current) {
      setToast(null);
    }
  }, []);

  useEffect(() => {
    if (!panelCampaignId) {
      setPanelDetail(null);
      return undefined;
    }
    if (!panelOpen) {
      return undefined;
    }
    let cancelled = false;
    setPanelLoading(true);
    setFormError('');
    setForm({
      client_name: '',
      client_website: '',
      alternative_names: '',
      client_overview: '',
      core_offerings: '',
      additional_domains: '',
      do_not_contact_domains: '',
      enabled: true,
      frequency: 'weekly',
      preferred_weekday: 5,
      runs_per_period: 1,
      runs_on_preferred_day: 1,
      run_gap_hours: 1,
    });
    (async () => {
      try {
        const { data } = await api.get(`/aivis/campaigns/${panelCampaignId}/`);
        if (cancelled) return;
        setPanelDetail(data);
        setForm({
          client_name: data.client_name || '',
          client_website: data.client_website || '',
          alternative_names: data.alternative_names || '',
          client_overview: data.client_overview || '',
          core_offerings: data.core_offerings || '',
          additional_domains: Array.isArray(data.additional_domains) ? data.additional_domains.join('\n') : '',
          do_not_contact_domains: Array.isArray(data.do_not_contact_domains) ? data.do_not_contact_domains.join('\n') : '',
          enabled: data.enabled !== false,
          frequency: data.frequency || 'weekly',
          preferred_weekday: data.preferred_weekday ?? 5,
          runs_per_period: data.runs_per_period ?? 1,
          runs_on_preferred_day: data.runs_on_preferred_day ?? 1,
          run_gap_hours: data.run_gap_hours ?? 1,
        });
      } catch {
        if (!cancelled) setFormError('Could not load brand details.');
      } finally {
        if (!cancelled) setPanelLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [panelOpen, panelCampaignId]);

  useEffect(() => {
    if (panelOpen || !panelCampaignId) return undefined;
    const t = setTimeout(() => setPanelCampaignId(null), BRAND_PANEL_TRANSITION_MS);
    return () => clearTimeout(t);
  }, [panelOpen, panelCampaignId]);

  function openEdit(id) {
    closeMenu();
    setPanelCampaignId(id);
    setPanelOpen(true);
  }

  function handleRemove(campaign) {
    closeMenu();
    setDeleteError('');
    setRemoveChoice('archive');
    setRemoveTarget({ campaign, mode: 'choice' });
  }

  function handleHardDelete(campaign) {
    closeMenu();
    setDeleteError('');
    setRemoveTarget({ campaign, mode: 'confirm-delete' });
  }

  async function confirmRemove() {
    if (!removeTarget) return;
    const { campaign, mode } = removeTarget;
    const label = campaign.client_name || campaign.name;
    setDeleteError('');
    setDeleting(true);
    try {
      if (mode === 'choice' && removeChoice === 'archive') {
        await api.patch(`/aivis/campaigns/${campaign.id}/archive/`, { archived: true });
        pushToast(tieClosingPeriod(`“${label}” was archived. Its data is preserved and it will not run again until unarchived.`));
      } else {
        await api.delete(`/aivis/campaigns/${campaign.id}/`);
        pushToast(tieClosingPeriod(`“${label}” was permanently deleted.`));
      }
      await refreshCampaigns();
      if (showArchived) await loadArchivedCampaigns();
      setRemoveTarget(null);
    } catch (e) {
      const d = e.response?.data;
      const msg = d?.detail || (typeof d === 'string' ? d : '') || 'Could not update client.';
      setDeleteError(msg);
    } finally {
      setDeleting(false);
    }
  }

  async function unarchiveCampaign(campaign) {
    closeMenu();
    try {
      await api.patch(`/aivis/campaigns/${campaign.id}/archive/`, { archived: false });
      pushToast(tieClosingPeriod(`“${campaign.client_name || campaign.name}” was unarchived and is active again.`));
      await refreshCampaigns();
      await loadArchivedCampaigns();
    } catch (e) {
      const d = e.response?.data;
      setDeleteError(d?.detail || 'Could not unarchive client.');
    }
  }

  async function handleSave() {
    if (!panelCampaignId) return;
    setFormError('');
    if (!form.client_overview.trim()) {
      setFormError('Client overview is required.');
      return;
    }
    if (!form.core_offerings.trim()) {
      setFormError('Core offering(s) is required.');
      return;
    }
    setSaving(true);
    const prevEnabled = panelDetail?.enabled !== false;
    try {
      const isWeeklyBased = form.frequency === 'weekly' || form.frequency === 'bi_weekly';
      const body = {
        enabled: form.enabled,
        client_name: form.client_name.trim(),
        client_website: form.client_website.trim(),
        alternative_names: form.alternative_names,
        client_overview: form.client_overview.trim(),
        core_offerings: form.core_offerings.trim(),
        additional_domains: linesToDomainList(form.additional_domains),
        do_not_contact_domains: linesToDomainList(form.do_not_contact_domains),
        frequency: form.frequency,
        preferred_weekday: isWeeklyBased ? Number(form.preferred_weekday) : null,
        runs_per_period: isWeeklyBased ? 1 : Math.max(1, Number(form.runs_per_period) || 1),
        runs_on_preferred_day: isWeeklyBased ? Math.max(1, Number(form.runs_on_preferred_day) || 1) : 1,
        run_gap_hours: Math.max(1, Number(form.run_gap_hours) || 1),
      };
      const { data: updated } = await api.patch(`/aivis/campaigns/${panelCampaignId}/`, body);
      await refreshCampaigns();
      const nowEnabled = updated.enabled !== false;
      if (prevEnabled !== nowEnabled) {
        toastVisibleRef.current = false;
        setToastVisible(false);
        const campaignLabel = updated.name || updated.client_name || 'Campaign';
        if (nowEnabled) {
          setToast({
            id: Date.now(),
            message: tieClosingPeriod(
              `“${campaignLabel}” Campaign is Live now and will run on ${formatCampaignSchedule(updated)}.`
            ),
          });
        } else {
          setToast({
            id: Date.now(),
            message: tieClosingPeriod(
              `“${campaignLabel}” Campaign is paused now and will not run until you activate it again.`
            ),
          });
        }
      }
      setPanelOpen(false);
    } catch (e) {
      const d = e.response?.data;
      const msg =
        (typeof d === 'object' && d && (d.detail || Object.values(d).flat().join(' '))) ||
        'Could not save changes.';
      setFormError(typeof msg === 'string' ? msg : 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  }

  const panelCover = useMemo(() => {
    if (!panelCampaignId) return 'var(--aivis-bg)';
    const c = panelDetail || sorted.find((x) => String(x.id) === String(panelCampaignId));
    if (!c) return 'var(--aivis-bg)';
    return brandCoverTint(c);
  }, [panelDetail, panelCampaignId, sorted]);

  if (!canView) {
    return <Navigate to="/ai-vis/home" replace />;
  }

  return (
    <div className="aivis-brands-page">
      <div className="aivis-brands-header">
        <h1 className="aivis-page-title aivis-brands-title">
          <AiVisBrandsIcon className="aivis-brands-title-icon" aria-hidden />
          Clients
        </h1>
        {showDelete && (
          <label className="aivis-toggle-switch" title="Show archived clients">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            <span className="aivis-toggle-switch-track" aria-hidden="true">
              <span className="aivis-toggle-switch-thumb" />
            </span>
            <span className="aivis-toggle-switch-label">Show archived</span>
          </label>
        )}
      </div>
      <p className="aivis-page-sub">
        Each client is shown as a card. Mentions count completed runs where the client was detected in AI responses.
      </p>

      {!sorted.length ? (
        <div className="aivis-brands-empty-state">
          <div className="aivis-brands-empty-icon" aria-hidden>
            <AiVisBrandsIcon />
          </div>
          <h3 className="aivis-brands-empty-title">No clients yet</h3>
          <p className="aivis-brands-empty-desc">Create a client to see them here.</p>
        </div>
      ) : (
        <section className="aivis-brands-section" aria-labelledby="aivis-brands-heading">
          <h2 id="aivis-brands-heading" className="aivis-brands-section-title">
            Clients
          </h2>
          <div className="aivis-brand-grid">
            {sorted.map((c) => (
              <BrandTile
                key={c.id}
                campaign={c}
                isSelected={String(selectedCampaignId) === String(c.id)}
                menuOpen={menuOpenId === c.id}
                onMenuToggle={() => setMenuOpenId((id) => (id === c.id ? null : c.id))}
                menuRef={(el) => {
                  menuRefs.current[c.id] = el;
                }}
                onEdit={() => openEdit(c.id)}
                onRemove={() => handleRemove(c)}
                showDelete={showDelete}
                showDeleteLocked={!showDelete}
              />
            ))}
          </div>
        </section>
      )}

      {showArchived && (
        <section className="aivis-brands-section" aria-labelledby="aivis-brands-archived-heading" style={{ marginTop: 28 }}>
          <h2 id="aivis-brands-archived-heading" className="aivis-brands-section-title">
            Archived clients
          </h2>
          {archivedLoading ? (
            <p className="aivis-page-sub">Loading archived clients…</p>
          ) : sortedArchived.length === 0 ? (
            <div className="aivis-card aivis-brands-empty">
              <p>No archived clients.</p>
            </div>
          ) : (
            <div className="aivis-brand-grid">
              {sortedArchived.map((c) => (
                <BrandTile
                  key={c.id}
                  campaign={c}
                  isSelected={String(selectedCampaignId) === String(c.id)}
                  menuOpen={menuOpenId === c.id}
                  onMenuToggle={() => setMenuOpenId((id) => (id === c.id ? null : c.id))}
                  menuRef={(el) => {
                    menuRefs.current[c.id] = el;
                  }}
                  onEdit={() => openEdit(c.id)}
                  onUnarchive={() => unarchiveCampaign(c)}
                  onHardDelete={() => handleHardDelete(c)}
                  showDelete={showDelete}
                  showDeleteLocked={!showDelete}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {removeTarget ? (
        <div
          className="aivis-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="aivis-remove-client-title"
          onClick={(e) => { if (e.target === e.currentTarget && !deleting) setRemoveTarget(null); }}
        >
          {removeTarget.mode === 'choice' ? (
            <div className="aivis-modal aivis-org-modal">
              <div className="aivis-org-del-header">
                <div className="aivis-org-del-icon" aria-hidden>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <line x1="12" y1="9" x2="12" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <line x1="12" y1="17" x2="12.01" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>
                <h2 id="aivis-remove-client-title" className="aivis-org-modal-title">
                  Remove “{removeTarget.campaign.client_name || removeTarget.campaign.name}”?
                </h2>
              </div>
              <p className="aivis-modal-body">Choose what should happen to this client:</p>
              <div className="aivis-org-del-options">
                <label className={`aivis-org-del-option ${removeChoice === 'archive' ? 'aivis-org-del-option--active' : ''}`}>
                  <input
                    type="radio"
                    name="remove-client-mode"
                    value="archive"
                    checked={removeChoice === 'archive'}
                    onChange={() => setRemoveChoice('archive')}
                  />
                  <div className="aivis-org-del-option-body">
                    <span className="aivis-org-del-option-title">Archive — keep data, no new runs</span>
                    <span className="aivis-org-del-option-desc">
                      Hides the client and stops future runs, but preserves all history (prompts, runs, executions,
                      sources). Reversible anytime via "Unarchive".
                    </span>
                  </div>
                </label>
                <label className={`aivis-org-del-option aivis-org-del-option--danger ${removeChoice === 'delete' ? 'aivis-org-del-option--active' : ''}`}>
                  <input
                    type="radio"
                    name="remove-client-mode"
                    value="delete"
                    checked={removeChoice === 'delete'}
                    onChange={() => setRemoveChoice('delete')}
                  />
                  <div className="aivis-org-del-option-body">
                    <span className="aivis-org-del-option-title">Delete permanently</span>
                    <span className="aivis-org-del-option-desc">
                      Removes the brand, prompts, runs, executions, and sources. <strong>This cannot be undone.</strong>
                    </span>
                  </div>
                </label>
              </div>
              {deleteError ? <div className="aivis-auth-error" style={{ margin: '12px 0 0' }}>{deleteError}</div> : null}
              <div className="aivis-wizard-actions aivis-wizard-actions--compact" style={{ marginTop: 20 }}>
                <button type="button" className="aivis-btn-secondary" disabled={deleting} onClick={() => setRemoveTarget(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className={removeChoice === 'delete' ? 'aivis-btn-danger' : 'aivis-btn-primary'}
                  disabled={deleting}
                  onClick={confirmRemove}
                >
                  {deleting
                    ? (removeChoice === 'delete' ? 'Deleting…' : 'Archiving…')
                    : (removeChoice === 'delete' ? 'Delete permanently' : 'Archive client')}
                </button>
              </div>
            </div>
          ) : (
            <div className="aivis-modal">
              <h3 id="aivis-remove-client-title" style={{ marginTop: 0, marginBottom: 10 }}>Delete client?</h3>
              <p className="aivis-modal-body">
                Delete campaign “{removeTarget.campaign.client_name || removeTarget.campaign.name}”? This removes the brand,
                prompts, runs, executions, and sources. This cannot be undone.
              </p>
              {deleteError ? <div className="aivis-auth-error" style={{ marginBottom: 12 }}>{deleteError}</div> : null}
              <div className="aivis-wizard-actions" style={{ marginTop: 0 }}>
                <button
                  type="button"
                  className="aivis-btn-secondary"
                  onClick={() => {
                    if (deleting) return;
                    setRemoveTarget(null);
                  }}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button type="button" className="aivis-btn-danger" onClick={confirmRemove} disabled={deleting}>
                  {deleting ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : null}

      <BrandEditPanel
        open={panelOpen}
        onClose={() => {
          if (saving) return;
          setPanelOpen(false);
        }}
        campaignId={panelCampaignId}
        coverTint={panelCover}
        initialDetail={panelDetail}
        form={form}
        setForm={setForm}
        saving={saving}
        loading={panelLoading}
        onSave={handleSave}
        formError={formError}
      />

      {toast
        ? createPortal(
            <div
              className={`aivis-toast-anchor ${toastVisible ? 'aivis-toast-anchor--visible' : ''}`}
              onTransitionEnd={onToastTransitionEnd}
            >
              <div className="aivis-toast" role="status" aria-live="polite">
                <p className="aivis-toast-text">{toast.message}</p>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
